import { PropTypes } from 'prop-types'
import { useState, useEffect } from 'react'
import { MFAService } from '../api/MFAFlowAPI'
import { MFA } from '../mfaWizard/MFASteps'
import './additional-security.scss'
import { Factors } from './Factors'
import { MFA_ENROLL_STATUS, PROFILE_UPDATE_MFA } from './../../constants'
import { NoFactors } from './NoFactors'
import { MFADomain } from '../domain/mfa.services'
import ConfirmPasswordModal from '../editProfileUI/ConfirmPasswordModal'
import { trackEventMessage, trackEventMessageFailure } from '../../TelemetryService'
import { EventModel } from '../../models/EventModel'
import { removeAskMeLaterLocalStorageItem, removeMfaRememberLocalStorageItem } from '../../utils/localStorageUtils'

export const AdditionalSecurity = ( {
  webId,
  onFactorAdd,
  onSetUp,
  config,
  showErrorMessage,
  loginEmailFocusClick,
  updateFactors 
} ) => {
  const [ factors, setFactors ] = useState( [] )
  const [ userId, setUserId ] = useState( '' )
  const [ mfaIsOnForUser, setMfaIsOnForUser ] = useState( false )
  const [ modalOpen, setModalOpen ] = useState( false )
  const [ loading, setLoading ] = useState( false )
  const [ setUpLoading, setSetUpLoading ] = useState( false )
  const [ modalHandler, setModalHandler ] = useState( () => () => console.log( 'default function' ) )

  const getFactorsAndUserId = async () => {
    const userIdValue = MFADomain.getOktaEncryptedUserId()

    setUserId( userIdValue )
    let userFactors = await MFAService.GetUserFactors( userIdValue )
    userFactors = userFactors.filter( e => e.status === MFA_ENROLL_STATUS.ACTIVE )
    updateFactors ( userFactors )

    setFactors( userFactors )
    setMfaIsOnForUser( userFactors.length > 0 )
  }

  const openConfirmationModal=( event )=>{
      if( event ) event.preventDefault()
      setModalOpen( true ) 
  }
  
  const closeConfirmationModal=( )=>{
      setModalOpen( false ) 
  }

  const handleModalOpenClose=( open )=>{
      if( open ) openConfirmationModal( )
      else closeConfirmationModal()
  } 

  const resetFactor = async ( factorId ) => {
    closeConfirmationModal()
    try {
      const resetResponse = await MFAService.ResetFactor( userId, factorId )
      if ( resetResponse ) {
        await getFactorsAndUserId()
        trackEventMessage( new EventModel( PROFILE_UPDATE_MFA.REMOVE, webId ) ) 
        removeAskMeLaterLocalStorageItem() // when the user removes a factor the MFA Ask me again later item gets removed
        return true
      }
    } catch ( error ) {
      trackEventMessageFailure( new EventModel( PROFILE_UPDATE_MFA.REMOVE_FAILURE, webId ), error )
    }
    return false
  }
  
  const handleResetFactor = async ( factorId ) => {
    setModalOpen( true ) 
    setModalHandler( ()=>()=> resetFactor( factorId ) )
  }
  const triggerLoginEmailFocus = ( ) => {  
    loginEmailFocusClick( )
  }

  const resetAllFactors = async () => {    
      setModalOpen( false ) 
    try {
      const resetResponse = await MFAService.ResetAllFactors( userId )
      if ( resetResponse ) {
        await getFactorsAndUserId()
        trackEventMessage( new EventModel( PROFILE_UPDATE_MFA.TURN_OFF, webId ) )
        removeMfaRememberLocalStorageItem()
        removeAskMeLaterLocalStorageItem()
        closeConfirmationModal()
        return true
      }
    } catch ( error ) {
      trackEventMessageFailure( new EventModel( PROFILE_UPDATE_MFA.TURN_OFF_FAILURE, webId ), error )
    }
    return false
  }

  const handleResetAllFactor = async (  ) => {
    setModalOpen( true ) 
    setModalHandler( ()=>()=> resetAllFactors( ) )
  }
    
  const handleFactorAddClick = async ( selectedFactor, update ) => {
    await getFactorsAndUserId()
    window.scrollTo( 0, 0 )
    setLoading( false ) 
    if( !update ){
      setModalOpen( true ) 
      setModalHandler( ()=>()=> onFactorAdd( factors, selectedFactor, update ) )
      trackEventMessage( new EventModel( PROFILE_UPDATE_MFA.ADD, webId ) )
    }else{
      onFactorAdd( factors, selectedFactor, update )
      removeAskMeLaterLocalStorageItem() // when the user adds a factor the MFA Ask me again later item gets removed
    }
  }

  const handleSetupClick = async () => {
    setSetUpLoading( true )
    await getFactorsAndUserId()
    window.scrollTo( 0, 0 )
    trackEventMessage( new EventModel( PROFILE_UPDATE_MFA.SET_UP, webId ) )
    setModalOpen( true ) 
    setModalHandler( ()=>()=> onSetUp( factors ) ) 
    setSetUpLoading( false )
  }

  const handleSmsUpdate = ( id ) => {
    const smsFactor = factors.find( ( f ) => f.factorType === MFA.factorType.sms )
    resetFactor( smsFactor?.id )
    handleFactorAddClick( MFA.FACTORS.SMS, true )
    trackEventMessage( new EventModel( PROFILE_UPDATE_MFA.EDIT, webId ) )
    setLoading( false )
  }

  const handleEditClick = ( id ) => {    
    setModalOpen( true )
    setModalHandler( ()=>()=> handleSmsUpdate( id ) )
  }

  useEffect( async () => {
    await getFactorsAndUserId().catch( console.error )
  }, [] )

  const renderInner = () => {
    if ( factors?.length )
      return (
          <Factors
          webId={ webId } data-testid="factors"
          showErrorMessage={ showErrorMessage }
          factors={ factors }
          userId={ userId }
          onHandleUpdate={ ( factorId ) => {
            handleEditClick( factorId ) 
          } }
          onResetFactor={ ( factorId ) => {
            handleResetFactor( factorId )
          } }
          onResetAllFactors={ () => {
            handleResetAllFactor()
          } }
          mfaIsOnForUser={ mfaIsOnForUser }
          onFactorAddClick={ handleFactorAddClick }
          config={ config }
          loginEmailFocusClick = { ( ) => { triggerLoginEmailFocus( ) } }
        ></Factors>
      )
    else
      return (
          <NoFactors
          webId={ webId } loading={ setUpLoading } data-testid="noFactors"
          onSetUp={ () => handleSetupClick() }
        ></NoFactors>
      )
  }

  return (
      <>
          <section id="adsec-parent" data-testid="additional-security">
              <div className="label-icon-box additional-security" role='heading'>
                  <h2
            className="c-form-intro c-form-intro--long"
            tabIndex="0"
            aria-label={ 'Additional Security Settings' }
          >
                      Additional Security Settings
                  </h2>
                  {renderInner()}
              </div>
          </section>
          <ConfirmPasswordModal
            open={ modalOpen }
            loading={ loading } 
            onSubmit={   modalHandler  }
            changeLoading={ ( value ) => setLoading( value ) }
            webId={ config.userProfileDataViewModel?.webId }
            handleModalOpenClose={ handleModalOpenClose }
            showErrorMessage={ showErrorMessage }
    />
      </>
  )
}

AdditionalSecurity.propTypes = {
  webId: PropTypes.string,
  onFactorAdd: PropTypes.func,
  onSetUp: PropTypes.func,
  openConfirmModal: PropTypes.func,
  showErrorMessage: PropTypes.func,
  loginEmailFocusClick: PropTypes.func,
  config: PropTypes.shape( {
    userProfileDataViewModel: PropTypes.object,
    forgotPasswordUrl: PropTypes.string,
    logInApiUrl: PropTypes.string,
    disablePhoneNumber: PropTypes.bool,
    logOffUrl: PropTypes.string,
    contactUsUrl: PropTypes.string,
    webId: PropTypes.string,
    antiForgeryHeaderName: PropTypes.string 
  } ), 
  updateFactors: PropTypes.func
}
